// Sugalarm — kiosk-local Wi-Fi agent client (cycle 56).
//
// These calls hit http://127.0.0.1:8765 directly, NOT the Fly API
// origin via apiClient. The agent runs on the Pi alongside Chromium;
// see kiosk_agent/README.md and KIOSK_SETUP.md section 9c.
//
// Modeled on web/src/api/volume.ts — same direct fetch + AbortController
// timeout + ApiResult shape. The agent is gracefully unavailable on any
// non-kiosk surface (network error → "agent unreachable").

import type { ApiError, ApiErrorKind, ApiResult } from "../lib/types";

const AGENT_BASE = "http://127.0.0.1:8765";
const TIMEOUT_MS = 1500;
// The connect endpoint can legitimately take tens of seconds — the
// agent serialises a real nmcli activation. Give it a generous bound;
// the agent's own timeout (SUGALARM_AGENT_WIFI_CONNECT_TIMEOUT_SECONDS,
// default 45) is the authority. Scan can also take a few seconds when
// nmcli rescans the air.
const CONNECT_TIMEOUT_MS = 60_000;
const SCAN_TIMEOUT_MS = 15_000;

export interface WifiStatus {
  connected: boolean;
  ssid: string | null;
  ip: string | null;
  internet: boolean;
}

export interface WifiNetwork {
  ssid: string;
  signal: number;
  security: string;
  in_use: boolean;
}

export interface WifiConnectBody {
  ssid: string;
  password?: string;
  hidden?: boolean;
}

export interface WifiConnectResult {
  ok: boolean;
  connected: boolean;
  internet: boolean;
  ssid?: string | null;
  error?: string | null;
}

export interface AgentHealth {
  ok: boolean;
  control: string;
  version: string;
  backend: string;
  wifi_supported: boolean;
}

function asError(kind: ApiErrorKind, status: number, message: string): ApiError {
  return { kind, status, message };
}

async function call<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs: number = TIMEOUT_MS,
): Promise<ApiResult<T>> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${AGENT_BASE}${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers ?? {}),
      },
    });
    if (!res.ok) {
      const kind: ApiErrorKind =
        res.status === 422
          ? "validation"
          : res.status === 503
          ? "forbidden"
          : res.status >= 500
          ? "server"
          : "unknown";
      return {
        ok: false,
        error: asError(kind, res.status, `agent returned ${res.status}`),
      };
    }
    const data = (await res.json()) as T;
    return { ok: true, data };
  } catch {
    return {
      ok: false,
      error: asError("network", 0, "kiosk agent unreachable"),
    };
  } finally {
    window.clearTimeout(t);
  }
}

export function getHealth(): Promise<ApiResult<AgentHealth>> {
  return call<AgentHealth>("/health");
}

export function getStatus(): Promise<ApiResult<WifiStatus>> {
  return call<WifiStatus>("/wifi/status");
}

export function scan(): Promise<ApiResult<WifiNetwork[]>> {
  return call<WifiNetwork[]>("/wifi/scan", {}, SCAN_TIMEOUT_MS);
}

export function connect(
  body: WifiConnectBody,
): Promise<ApiResult<WifiConnectResult>> {
  return call<WifiConnectResult>(
    "/wifi/connect",
    { method: "POST", body: JSON.stringify(body) },
    CONNECT_TIMEOUT_MS,
  );
}
